import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { authConstants } from "@/domain/auth/auth.constant";
import { authQueries } from "@/domain/auth/auth.query";

const verifySearchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute("/(public)/(auth)/verify")({
  component: RouteComponent,
  validateSearch: verifySearchSchema,
  beforeLoad: ({ search }) => {
    // Si pas de token, rediriger vers l'accueil
    if (!search.token) {
      throw redirect({
        to: "/",
      });
    }
  },
  loaderDeps: ({ search }) => ({ token: search.token ?? "" }),
  loader: async ({ context: { queryClient }, deps: { token } }) =>
    queryClient.ensureQueryData(authQueries.verifyEmail(token)).catch(() => {
      throw redirect({
        to: "/",
        params: { error: "Le lien de vérification est invalide ou a expiré." },
      });
    }),
});

function RouteComponent() {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(
    authConstants.verifyRedirectCountdown,
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          navigate({ to: "/dashboard" });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="font-h1 text-green-600 mb-4">Email vérifié !</h1>
        <p className="font-p text-muted-foreground mb-8">
          Votre adresse email a été vérifiée avec succès. Vous allez être
          redirigé vers votre dashboard dans {countdown} seconde
          {countdown > 1 ? "s" : ""}.
        </p>
        <div className="space-y-4">
          <Button asChild className="w-full">
            <Link to="/dashboard">Accéder au dashboard</Link>
          </Button>
          <Button asChild variant="outline" className="w-full">
            <Link to="/">Retour à l'accueil</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
